import type { UIMessage } from "ai";
import type { HitlQuestion } from "./HitlOptions";

/**
 * Which human-in-the-loop question is open on a thread, read the same way by
 * every screen that answers one: the agent page and the onboarding builds.
 * Two copies of this rule disagreed once, and onboarding showed "the agent
 * stopped" with no question while the agent page showed it.
 */
export const HITL_TOOL_PART_TYPES = new Set([
  "tool-present_user_options",
  "tool-review_field_mappings",
  "tool-review_test_cases",
  "tool-acquire_oauth_credentials",
  "tool-request_user_connection",
  "tool-propose_configuration",
]);

export function findLatestOpenHitl(messages: UIMessage[]): { messageIndex: number; partIndex: number } | null {
  let latest: { messageIndex: number; partIndex: number } | null = null;
  for (let mi = 0; mi < messages.length; mi++) {
    const m = messages[mi];
    if (m.role === "user" && latest && mi > latest.messageIndex) {
      latest = null;
    }
    for (let pi = 0; pi < m.parts.length; pi++) {
      const p = m.parts[pi];
      if (HITL_TOOL_PART_TYPES.has(p.type as string)) {
        latest = { messageIndex: mi, partIndex: pi };
      }
    }
  }
  return latest;
}

/** The open question's tool name and output envelope, or null when nothing waits on the person. */
export function openHitl(messages: UIMessage[]): { toolName: string; output: Record<string, unknown> } | null {
  const at = findLatestOpenHitl(messages);
  if (!at) return null;
  const part = messages[at.messageIndex]!.parts[at.partIndex] as { type: string; output?: unknown };
  if (!part.output || typeof part.output !== "object") return null;
  return { toolName: part.type.slice("tool-".length), output: part.output as Record<string, unknown> };
}

// Accept either the current `{ questions: [...] }` payload or legacy
// single-question shape `{ question, header, multiSelect, options }` so
// in-flight HITL calls in already-streamed threads still render after deploy.
export function normalizeHitlQuestions(output: Record<string, unknown>): HitlQuestion[] {
  if (Array.isArray(output["questions"])) {
    return (output["questions"] as HitlQuestion[]).filter(
      (q) => q && typeof q.question === "string" && Array.isArray(q.options),
    );
  }
  if (typeof output["question"] === "string" && Array.isArray(output["options"])) {
    return [
      {
        question: String(output["question"]),
        header: String(output["header"] ?? "Choose"),
        multiSelect: Boolean(output["multiSelect"]),
        options: (output["options"] as Array<Record<string, unknown>>).map((o) => ({
          label: String(o["label"] ?? o["value"] ?? ""),
          description: String(o["description"] ?? ""),
        })),
      },
    ];
  }
  return [];
}
